import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["slots", "totalCost", "balance", "warning",
                    "testUrlLabel", "confirmDialog", "publishFlag",
                    "saveDraftBtn", "saveDraftLabel", "saveDraftSpinner", "startBtn", "startBtnMobile",
                    "savingDialog", "savingStepLabel", "savingStepMeta", "savingProgress", "savingDots",
                    "publishBlockHint"]
  static values = {
    balance: Number,
    appPublishReady: { type: Boolean, default: true },
    testUrlLabels: { type: Object, default: {} },
    savingStepLabels: { type: Array, default: [] },
    savingStepCounterTemplate: { type: String, default: "" }
  }

  connect() {
    this.updateTypeCards()
    this.update()
    this._savingTimer = null
    this._savingStepIndex = 0
    this._preventSavingDialogCancel = (e) => e.preventDefault()
    if (this.hasSavingDialogTarget) {
      this.savingDialogTarget.addEventListener("cancel", this._preventSavingDialogCancel)
    }
    this._onTurboSubmitEnd = this._onTurboSubmitEnd.bind(this)
    this._onCampaignFormChanged = () => this.update()
    document.addEventListener("turbo:submit-end", this._onTurboSubmitEnd)
    this.element.addEventListener("campaign-form:changed", this._onCampaignFormChanged)
  }

  disconnect() {
    this.element.removeEventListener("campaign-form:changed", this._onCampaignFormChanged)
    document.removeEventListener("turbo:submit-end", this._onTurboSubmitEnd)
    this.#stopSavingStepRotation()
    if (this.hasSavingDialogTarget) {
      this.savingDialogTarget.removeEventListener("cancel", this._preventSavingDialogCancel)
    }
  }

  update() {
    const slots       = parseInt(this.slotsTarget?.value || 5, 10)
    const costPerSlot = this.#costPerTester()
    const total       = slots * costPerSlot
    const balance     = this.balanceValue

    if (this.hasTotalCostTarget) {
      this.totalCostTargets.forEach(el => el.textContent = `${total} tokens`)
    }

    if (this.hasWarningTarget) {
      if (total > balance) {
        this.warningTarget.classList.remove("hidden")
        this.warningTarget.style.display = "block"
      } else {
        this.warningTarget.classList.add("hidden")
        this.warningTarget.style.display = "none"
      }
    }

    this.#syncStartButtonsState()
    this.#updateTestUrlLabel()
  }

  // ── Campaign type card visual toggle ──
  updateTypeCards() {
    const radios = this.element.querySelectorAll('input[name*="campaign_type"]')
    radios.forEach(radio => {
      const card      = this.element.querySelector(`[data-type-card="${radio.value}"]`)
      const nameEl    = this.element.querySelector(`[data-type-name="${radio.value}"]`)
      const badgeEl   = this.element.querySelector(`[data-type-badge="${radio.value}"]`)
      const indicator = this.element.querySelector(`[data-type-indicator="${radio.value}"]`)
      const checkEl   = this.element.querySelector(`[data-type-check="${radio.value}"]`)
      if (!card) return

      const on = radio.checked
      card.style.background   = on ? "rgba(var(--ls-accent-rgb),0.04)" : "#f8fafc"
      card.style.borderColor  = on ? "rgba(var(--ls-accent-rgb),0.35)" : "rgba(15,23,42,0.1)"

      if (nameEl)    nameEl.style.color    = on ? "var(--ls-accent)" : "rgba(15,23,42,0.75)"
      if (badgeEl) {
        badgeEl.style.background = on ? "rgba(var(--ls-accent-rgb),0.12)" : "rgba(15,23,42,0.07)"
        badgeEl.style.color      = on ? "var(--ls-accent)" : "rgba(15,23,42,0.5)"
      }
      if (indicator) {
        indicator.style.borderColor = on ? "var(--ls-accent)" : "rgba(15,23,42,0.2)"
        indicator.style.background  = on ? "var(--ls-accent)" : "transparent"
      }
      if (checkEl) checkEl.style.display = on ? "block" : "none"
    })

    const selected = this.element.querySelector('input[name*="campaign_type"]:checked')
    if (selected) {
      document.dispatchEvent(new CustomEvent("campaign-type:changed", {
        detail: { type: selected.value }
      }))
    }
  }

  // ── Loading state on save ──
  showLoading() {
    if (this.hasConfirmDialogTarget && this.confirmDialogTarget.open) {
      this.confirmDialogTarget.close()
    }

    if (this.hasSaveDraftLabelTarget) this.saveDraftLabelTarget.style.display = "none"
    if (this.hasSaveDraftSpinnerTarget) this.saveDraftSpinnerTarget.style.display = "block"
    if (this.hasSaveDraftBtnTarget) {
      this.saveDraftBtnTarget.disabled = true
      this.saveDraftBtnTarget.style.opacity = "0.6"
      this.saveDraftBtnTarget.style.cursor = "not-allowed"
    }
    this.#setStartButtonsSubmitting(true)

    this.#startSavingOverlay()
  }

  // ── Publish flow ──
  openPublishFlow(event) {
    event.preventDefault()
    if (this.#isInsufficient()) {
      this.openPaywall(event)
      return
    }
    if (this.#isPublishBlocked()) {
      this.#revealPublishBlockHint()
      return
    }
    this.#hidePublishBlockHint()
    if (this.hasConfirmDialogTarget) this.confirmDialogTarget.showModal()
  }

  // Submit the main form with action_intent=publish (create+publish in one step)
  submitAndPublish() {
    if (this.#isInsufficient()) return
    if (this.#isPublishBlocked()) {
      this.#revealPublishBlockHint()
      return
    }
    this.#hidePublishBlockHint()
    if (this.hasPublishFlagTarget) {
      this.publishFlagTarget.value = "publish"
    }
    const form = this.element.querySelector("form")
    if (form) form.requestSubmit()
  }

  // ── Paywall ──
  openPaywall(event) {
    event?.preventDefault()
    document.dispatchEvent(new CustomEvent("paywall:open"))
  }

  // ── Stepper ──
  increment() {
    const input = this.slotsTarget
    const max   = parseInt(input.max || 20, 10)
    const val   = parseInt(input.value || 1, 10)
    if (val < max) { input.value = val + 1; this.update() }
  }

  decrement() {
    const input = this.slotsTarget
    const min   = parseInt(input.min || 1, 10)
    const val   = parseInt(input.value || 1, 10)
    if (val > min) { input.value = val - 1; this.update() }
  }

  // ── File picker ──
  openFilePicker(event) {
    event.preventDefault()
    const targetId = event.currentTarget.dataset.fileTarget
    document.getElementById(targetId)?.click()
  }

  showFileName(event) {
    const labelId = event.currentTarget.dataset.labelId
    const label   = document.getElementById(labelId)
    if (!label) return
    const names = Array.from(event.currentTarget.files).map(f => f.name).join(", ")
    label.textContent = names
  }

  // Private

  _onTurboSubmitEnd(event) {
    const form = this.element.querySelector("form")
    if (!form || event.target !== form) return
    if (event.detail.success) return
    this.#dismissSavingOverlay()
  }

  #startSavingOverlay() {
    const labels = this.savingStepLabelsValue
    if (!this.hasSavingDialogTarget || !Array.isArray(labels) || labels.length === 0) return

    this.#stopSavingStepRotation()
    this._savingStepIndex = 0
    this.#renderSavingStep()

    try {
      this.savingDialogTarget.showModal()
    } catch (_) {
      /* ignore */
    }

    this._savingTimer = window.setInterval(() => {
      if (this._savingStepIndex < labels.length - 1) {
        this._savingStepIndex += 1
        this.#renderSavingStep()
      }
    }, 2400)
  }

  #renderSavingStep() {
    const labels = this.savingStepLabelsValue
    const i = this._savingStepIndex
    const label = labels[i]
    const total = labels.length

    if (this.hasSavingStepLabelTarget && label) {
      this.savingStepLabelTarget.textContent = label
    }

    if (this.hasSavingStepMetaTarget && total > 0) {
      const tpl = (this.savingStepCounterTemplateValue || "").trim()
      this.savingStepMetaTarget.textContent = tpl
        ? tpl.replaceAll("%{current}", String(i + 1)).replaceAll("%{total}", String(total))
        : `${i + 1} / ${total}`
    }

    if (this.hasSavingProgressTarget && total > 0) {
      const pct = ((i + 1) / total) * 100
      this.savingProgressTarget.style.width = `${pct}%`
    }

    if (this.hasSavingDotsTarget) {
      const dots = this.savingDotsTarget.querySelectorAll("[data-saving-dot]")
      dots.forEach((el, idx) => {
        const done = idx < i
        const current = idx === i
        if (done) {
          el.style.backgroundColor = "rgba(var(--ls-accent-rgb),0.35)"
          el.style.transform = "scale(1)"
          el.style.opacity = "1"
        } else if (current) {
          el.style.backgroundColor = "var(--ls-accent)"
          el.style.transform = "scale(1.35)"
          el.style.opacity = "1"
        } else {
          el.style.backgroundColor = "rgba(15,23,42,0.12)"
          el.style.transform = "scale(1)"
          el.style.opacity = "0.5"
        }
      })
    }
  }

  #stopSavingStepRotation() {
    if (this._savingTimer) {
      window.clearInterval(this._savingTimer)
      this._savingTimer = null
    }
  }

  #dismissSavingOverlay() {
    this.#stopSavingStepRotation()
    if (this.hasSavingDialogTarget && this.savingDialogTarget.open) {
      this.savingDialogTarget.close()
    }
    if (this.hasSaveDraftLabelTarget) this.saveDraftLabelTarget.style.display = ""
    if (this.hasSaveDraftSpinnerTarget) this.saveDraftSpinnerTarget.style.display = "none"
    if (this.hasSaveDraftBtnTarget) {
      this.saveDraftBtnTarget.disabled = false
      this.saveDraftBtnTarget.style.opacity = ""
      this.saveDraftBtnTarget.style.cursor = ""
    }
    this.#setStartButtonsSubmitting(false)
    this.#syncStartButtonsState()
  }

  /** Active / désactive les boutons « Démarrer » selon le solde et les prérequis (hors envoi en cours). */
  #syncStartButtonsState() {
    const insufficient = this.#isInsufficient()
    const blocked = this.#isPublishBlocked()
    this.#eachStartButton(btn => {
      if (btn.dataset.tokenCalculatorSubmitting === "true") return
      const off = insufficient || blocked
      btn.disabled = off
      btn.style.opacity = off ? "0.45" : ""
      btn.style.cursor = off ? "not-allowed" : ""
    })
    if (!blocked) this.#hidePublishBlockHint()
  }

  #setStartButtonsSubmitting(on) {
    this.#eachStartButton(btn => {
      if (on) {
        btn.dataset.tokenCalculatorSubmitting = "true"
        btn.disabled = true
        btn.style.opacity = "0.4"
        btn.style.cursor = "not-allowed"
      } else {
        delete btn.dataset.tokenCalculatorSubmitting
      }
    })
  }

  #eachStartButton(fn) {
    if (this.hasStartBtnTarget) fn(this.startBtnTarget)
    if (this.hasStartBtnMobileTarget) fn(this.startBtnMobileTarget)
  }

  #costPerTester() {
    const selected = this.element.querySelector('input[name*="campaign_type"]:checked')
    if (!selected) return 1
    return selected.value === "complete" ? 3 : 1
  }

  #isInsufficient() {
    const slots = parseInt(this.slotsTarget?.value || 5, 10)
    const cost  = slots * this.#costPerTester()
    return cost > this.balanceValue
  }

  /** Fiche app (URL, langues, plateformes) — valeur serveur, figée pour cette page. */
  #isPublishBlocked() {
    if (!this.appPublishReadyValue) return true
    return !this.#campaignFormReadyForPublish()
  }

  #campaignFormReadyForPublish() {
    const form = this.element.querySelector("form")
    if (!form) return false

    if (form.querySelector("[data-over-limit='true']")) return false

    const name = form.querySelector("#test_campaign_name, input[name=\"test_campaign[name]\"]")?.value?.trim()
    const testUrl = form.querySelector("#test_campaign_test_url, input[name=\"test_campaign[test_url]\"]")?.value?.trim()
    if (!name || !testUrl) return false

    if (!form.querySelector("input[name=\"test_campaign[platform]\"]:checked")) return false
    if (!form.querySelector("input[name=\"test_campaign[campaign_type]\"]:checked")) return false

    const stepsHost = form.querySelector("[data-test-steps-target=\"container\"]")
    if (!stepsHost) return false
    const stepRows = stepsHost.querySelectorAll(".step-row:not([style*=\"display: none\"])")
    const titledSteps = Array.from(stepRows).filter(row => {
      const t = row.querySelector("input[name*=\"[title]\"]")?.value?.trim()
      return !!t
    })
    if (titledSteps.length < 2) return false

    const reportContainer = form.querySelector("[data-report-fields-target=\"container\"]")
    if (!reportContainer) return false
    const fieldRows = reportContainer.querySelectorAll("[data-report-field-row]:not([style*=\"display: none\"])")
    if (fieldRows.length === 0) return false
    for (const row of fieldRows) {
      const labelInput = row.querySelector("input[name*=\"[label]\"]")
      if (!labelInput?.value?.trim()) return false
    }

    return true
  }

  #revealPublishBlockHint() {
    if (!this.hasPublishBlockHintTarget) return
    const el = this.publishBlockHintTarget
    el.style.display = "flex"
    el.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  #hidePublishBlockHint() {
    if (!this.hasPublishBlockHintTarget) return
    this.publishBlockHintTarget.style.display = "none"
  }

  #updateTestUrlLabel() {
    if (!this.hasTestUrlLabelTarget) return
    const part = this.testUrlLabelTarget.querySelector("[data-token-calculator-dynamic-label]")
    if (!part) return

    const labels = this.testUrlLabelsValue || {}
    const input = this.element.querySelector('input[name*="[platform]"]:checked:not(:disabled)')
    const platform = input?.value
    const specific = platform && labels[platform]
    part.textContent = specific || labels.default || part.textContent.trim()
  }
}
